import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import AppShell from "../components/layout/AppShell";
import Card from "../components/common/Card";
import Button from "../components/common/Button";
import Badge from "../components/common/Badge";
import { useAuth } from "../contexts/AuthContext";
import { getLatestRequestForUser, submitAccessRequest } from "../services/accessRequestService";
import { formatDateTime } from "../utils/format";
import { useToast } from "../contexts/ToastContext";

export default function RequestAccessPage() {
  const { user, profile } = useAuth();
  const { push } = useToast();
  const [reason, setReason] = useState("");
  const [request, setRequest] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    async function load() {
      if (user?.uid) {
        const req = await getLatestRequestForUser(user.uid);
        setRequest(req);
      }
    }
    load();
  }, [user?.uid]);

  if (profile?.role === "admin") return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (reason.length < 20) return;
    setSubmitting(true);
    try {
      await submitAccessRequest({ uid: user.uid, email: user.email, displayName: profile.displayName }, reason);
      push("Access request submitted", "success");
      setReason("");
      const req = await getLatestRequestForUser(user.uid);
      setRequest(req);
    } catch (error) {
      if (error.code === "access/request-already-pending") {
        push("A pending access request already exists.", "info");
        const req = await getLatestRequestForUser(user.uid);
        setRequest(req);
      } else {
        push("Unable to submit request right now.", "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell title="Request Admin Access">
      <Card style={{ maxWidth: 520, margin: "0 auto" }}>
        <h2 className="h2">Request Admin Access</h2>
        <p className="muted">
          Admin access allows you to view, download, and manage patient documents. Your request will be reviewed by an existing
          administrator.
        </p>
        {request?.status ? (
          <Card style={{ marginBottom: 12 }}>
            <div className="row-between">
              <span>Latest request status</span>
              <Badge tone={request.status === "approved" ? "success" : request.status === "rejected" ? "error" : "pending"}>
                {request.status}
              </Badge>
            </div>
            <p className="muted">Submitted {formatDateTime(request.requestedAt)}</p>
          </Card>
        ) : null}
        {request?.status === "pending" ? null : (
          <form onSubmit={submit}>
            <div className="form-row">
              <label>Reason for request</label>
              <textarea value={reason} minLength={20} maxLength={500} rows={5} onChange={(e) => setReason(e.target.value)} required />
              <div className="muted">{reason.length}/500</div>
            </div>
            <Button disabled={reason.length < 20 || submitting}>{submitting ? "Submitting..." : "Submit Request"}</Button>
          </form>
        )}
      </Card>
    </AppShell>
  );
}
